import {
  extractAxiosErrorMessage,
  PromptAddendumModel,
  PromptRuleModel,
  TaskPromptData,
  TaskPromptDiffResponse,
  TaskPromptRevision,
} from "@inrange/building-manager-api-client";
import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal } from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

// The editable rule-set prompt shell, shared by every rule-based-agent Data & Config
// card (landlord research, contacts ranking, outreach email). It owns the parts that
// are identical across tasks - the preamble editor, rule CRUD + reorder, the enforced
// footer, the compiled preview, and the revision history + diff - and takes the
// task-specific bits as props: the descriptive copy, the already-bound write callbacks
// (so it stays task-agnostic), the diff hook, and a `renderTestPanel` slot handed the
// live draft addendum. This is the single source the three cards run through, so the
// shell can't drift between them the way three near-copies would.

// The content fields an operator edits on a rule (improvement is operator-only,
// never sent to the model).
interface RuleDraft {
  category: string;
  title: string;
  guidance: string;
  why: string;
  limits: string;
  evidence: string;
  improvement: string;
  status: "active" | "draft" | "retired";
}

const RULE_TEXT_FIELDS: { key: keyof RuleDraft; label: string; rows: number }[] =
  [
    { key: "guidance", label: "Guidance (the instruction - required)", rows: 3 },
    { key: "why", label: "Why (the generalisation behind it)", rows: 2 },
    { key: "limits", label: "Limits (where it stops applying)", rows: 2 },
    { key: "evidence", label: "Evidence (the cases behind it)", rows: 2 },
    {
      key: "improvement",
      label: "Improvement (operator-only - never sent to the model)",
      rows: 2,
    },
  ];

const draftFromRule = (r: PromptRuleModel): RuleDraft => ({
  category: r.category,
  title: r.title,
  guidance: r.guidance,
  why: r.why,
  limits: r.limits,
  evidence: r.evidence,
  improvement: r.improvement,
  status: r.status,
});

const emptyDraft = (): RuleDraft => ({
  category: "",
  title: "",
  guidance: "",
  why: "",
  limits: "",
  evidence: "",
  improvement: "",
  status: "active",
});

const revisionLabel = (r: TaskPromptRevision): string => {
  const who = r.isSeed ? "built-in default" : `saved by ${r.createdBy}`;
  const when = r.createdAt
    ? ` on ${new Date(r.createdAt).toLocaleString()}`
    : "";
  return `Revision ${r.revisionNumber} - ${who}${when}${
    r.isActive ? " (active)" : ""
  }`;
};

const statusVariant = (s: string): string =>
  s === "active" ? "success" : s === "draft" ? "secondary" : "dark";

export interface PromptRuleSetEditorProps {
  // Intro copy describing what this task's prompt drives.
  description: React.ReactNode;
  // Label on the compiled-preview panel ("...what the agent sees" vs "...the ranker").
  compiledPreviewLabel: string;
  // Message shown when the prompt can't be loaded.
  loadFailedMessage: string;
  // Placeholder for the rule Category field (task-specific examples).
  ruleCategoryPlaceholder: string;
  prompt: {
    isLoading: boolean;
    isError: boolean;
    data: TaskPromptData | undefined;
    refetch: () => Promise<unknown>;
  };
  // Any write in flight (disables the controls to avoid a stale-base second click).
  writing: boolean;
  saveRuleLoading: boolean;
  savePreambleLoading: boolean;
  // Already task/role-bound write callbacks - the editor stays task-agnostic.
  onSaveRule: (args: {
    baseRevision: number;
    ruleId?: string | null;
    rule: {
      category: string;
      title: string;
      guidance: string;
      why: string;
      limits: string;
      evidence: string;
      improvement: string;
    };
    status: string;
  }) => Promise<unknown>;
  onDeleteRule: (args: {
    baseRevision: number;
    ruleId: string;
  }) => Promise<unknown>;
  onReorderRules: (args: {
    baseRevision: number;
    ruleIds: string[];
  }) => Promise<unknown>;
  onSavePreamble: (args: {
    baseRevision: number;
    preamble: string;
  }) => Promise<unknown>;
  onResetToDefault: (args: {
    baseRevision: number;
    addendum: PromptAddendumModel;
    note: string;
  }) => Promise<unknown>;
  onRestoreRevision: (args: {
    baseRevision: number;
    revisionNumber: number;
  }) => Promise<unknown>;
  // Diff two revisions (already task/role-bound).
  useDiff: (
    fromRevision: number | null,
    toRevision: number | null,
    enabled: boolean
  ) => {
    isLoading: boolean;
    isError: boolean;
    data: TaskPromptDiffResponse | undefined;
  };
  // Task-specific test panel, handed the live draft (saved rules + unsaved preamble).
  renderTestPanel: (draftAddendum: PromptAddendumModel) => React.ReactNode;
  // Optional guidance shown when a revision has no structured rules.
  emptyRulesHint?: React.ReactNode;
}

const PromptRuleSetEditor: React.FC<PromptRuleSetEditorProps> = ({
  description,
  compiledPreviewLabel,
  loadFailedMessage,
  ruleCategoryPlaceholder,
  prompt,
  writing,
  saveRuleLoading,
  savePreambleLoading,
  onSaveRule,
  onDeleteRule,
  onReorderRules,
  onSavePreamble,
  onResetToDefault,
  onRestoreRevision,
  useDiff,
  renderTestPanel,
  emptyRulesHint,
}) => {
  const active = prompt.data?.active;
  const seedVersion = prompt.data?.seedVersion;
  const supersededRevision = prompt.data?.supersededRevision ?? null;
  // The concurrency token stays the highest SAVED revision even when a newer shipped seed
  // is being served, so a re-save appends the next number instead of colliding with the
  // superseded one. The backend reports that number on the superseded record; `active` is
  // the seed (revision 1) in that case, so reading it from there would be wrong.
  const baseRevision =
    supersededRevision?.revisionNumber ?? active?.revisionNumber ?? 1;
  const rules = useMemo(
    () =>
      [...(active?.addendum.rules ?? [])].sort((a, b) => a.position - b.position),
    [active?.addendum.rules]
  );

  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Any write runs through here: on a stale-revision 409 (or any error) it shows the
  // message and refetches, so the base resyncs and the operator can re-apply. On
  // success it shows a short confirmation.
  const runWrite = async (fn: () => Promise<unknown>, ok: string) => {
    setError(null);
    setNote(null);
    try {
      await fn();
      setNote(ok);
    } catch (e) {
      setError(
        (await extractAxiosErrorMessage(e)) ?? "Could not save the prompt."
      );
      await prompt.refetch();
    }
  };

  // --- Preamble -------------------------------------------------------------
  const [preambleDraft, setPreambleDraft] = useState<string>("");
  // Seed off the server preamble VALUE, not the revision number: a rule / reorder /
  // delete write bumps the revision but leaves the preamble unchanged, so keying on
  // the revision would reseed and silently discard an in-progress preamble edit.
  // `null` marks "not yet seeded" so an empty server preamble still seeds once.
  const [seededPreamble, setSeededPreamble] = useState<string | null>(null);
  useEffect(() => {
    if (active && active.addendum.preamble !== seededPreamble) {
      setPreambleDraft(active.addendum.preamble);
      setSeededPreamble(active.addendum.preamble);
    }
  }, [active, seededPreamble]);
  const preambleChanged =
    active !== undefined &&
    preambleDraft.trim() !== active.addendum.preamble.trim();

  // --- Rule editing ---------------------------------------------------------
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [ruleDraft, setRuleDraft] = useState<RuleDraft>(emptyDraft());
  const [addingRule, setAddingRule] = useState<boolean>(false);

  const startEdit = (r: PromptRuleModel) => {
    setAddingRule(false);
    setEditingRuleId(r.rule_id);
    setRuleDraft(draftFromRule(r));
  };
  const startAdd = () => {
    setEditingRuleId(null);
    setAddingRule(true);
    setRuleDraft(emptyDraft());
  };
  const cancelRuleEdit = () => {
    setEditingRuleId(null);
    setAddingRule(false);
  };

  const saveRuleDraft = async () => {
    if (!ruleDraft.guidance.trim()) {
      setError("Guidance is required - it is the instruction itself.");
      return;
    }
    await runWrite(
      () =>
        onSaveRule({
          baseRevision,
          ruleId: editingRuleId ?? undefined,
          rule: {
            category: ruleDraft.category,
            title: ruleDraft.title,
            guidance: ruleDraft.guidance,
            why: ruleDraft.why,
            limits: ruleDraft.limits,
            evidence: ruleDraft.evidence,
            improvement: ruleDraft.improvement,
          },
          status: ruleDraft.status,
        }),
      editingRuleId ? `Saved ${editingRuleId}.` : "Added a new rule."
    );
    cancelRuleEdit();
  };

  const removeRule = async (ruleId: string) => {
    if (
      !window.confirm(
        `Delete rule ${ruleId}? (Recoverable by restoring a past revision.)`
      )
    )
      return;
    await runWrite(
      () => onDeleteRule({ baseRevision, ruleId }),
      `Deleted ${ruleId}.`
    );
    if (editingRuleId === ruleId) cancelRuleEdit();
  };

  const moveRule = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rules.length) return;
    const order = rules.map((r) => r.rule_id);
    [order[index], order[target]] = [order[target], order[index]];
    await runWrite(
      () => onReorderRules({ baseRevision, ruleIds: order }),
      "Reordered rules."
    );
  };

  const savePreamble = () =>
    runWrite(
      () => onSavePreamble({ baseRevision, preamble: preambleDraft }),
      "Saved the preamble."
    );

  const resetToDefault = () => {
    if (!prompt.data) return;
    if (
      !window.confirm(
        "Reset the whole prompt to the shipped default? This appends a new revision (the current one stays in history)."
      )
    )
      return;
    void runWrite(
      () =>
        onResetToDefault({
          baseRevision,
          addendum: prompt.data!.defaultSeedAddendum,
          note: "Reset to shipped default",
        }),
      "Reset to the shipped default."
    );
  };

  // --- Revision history + diff ----------------------------------------------
  // Default the picker to the active revision (what's in view), re-seeding after a
  // write bumps it. A manual pick sticks until the next write (baseRevision, the only
  // dep, changes only when the active revision does).
  const [restoreOf, setRestoreOf] = useState<number>(0);
  useEffect(() => {
    setRestoreOf(baseRevision);
  }, [baseRevision]);
  const restore = () => {
    if (!restoreOf) return;
    void runWrite(
      () => onRestoreRevision({ baseRevision, revisionNumber: restoreOf }),
      `Restored revision ${restoreOf}.`
    );
  };

  // Diff a chosen revision against the previous one in the (newest-first) history.
  const [diffTo, setDiffTo] = useState<number | null>(null);
  const diffFrom = useMemo(() => {
    if (diffTo === null || !prompt.data) return null;
    const lower = prompt.data.revisions
      .map((r) => r.revisionNumber)
      .filter((n) => n < diffTo)
      .sort((a, b) => b - a);
    return lower.length > 0 ? lower[0] : null;
  }, [diffTo, prompt.data]);
  const diffQuery = useDiff(diffFrom, diffTo, diffTo !== null);

  const draftAddendum: PromptAddendumModel | null = active
    ? {
        preamble: preambleDraft,
        rules: active.addendum.rules,
        next_rule_seq: active.addendum.next_rule_seq,
      }
    : null;

  const renderRuleEditor = (idPrefix: string) => (
    <div
      style={{
        border: "1px solid #cfe2ff",
        borderRadius: 4,
        padding: 10,
        background: "#f8fbff",
      }}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Title</Form.Label>
          <Form.Control
            size="sm"
            value={ruleDraft.title}
            placeholder="short label"
            onChange={(e) => setRuleDraft({ ...ruleDraft, title: e.target.value })}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>
            Category
          </Form.Label>
          <Form.Control
            size="sm"
            value={ruleDraft.category}
            placeholder={ruleCategoryPlaceholder}
            onChange={(e) =>
              setRuleDraft({ ...ruleDraft, category: e.target.value })
            }
          />
        </div>
        <div style={{ width: 130 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>
            Status
          </Form.Label>
          <Form.Select
            size="sm"
            value={ruleDraft.status}
            onChange={(e) =>
              setRuleDraft({
                ...ruleDraft,
                status: e.target.value as RuleDraft["status"],
              })
            }
          >
            <option value="active">active</option>
            <option value="draft">draft</option>
            <option value="retired">retired</option>
          </Form.Select>
        </div>
      </div>
      {RULE_TEXT_FIELDS.map((f) => (
        <div key={f.key} style={{ marginBottom: 6 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>
            {f.label}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={f.rows}
            value={ruleDraft[f.key] as string}
            style={{ fontSize: 12 }}
            onChange={(e) =>
              setRuleDraft({ ...ruleDraft, [f.key]: e.target.value })
            }
          />
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          size="sm"
          variant="primary"
          disabled={!ruleDraft.guidance.trim() || writing}
          onClick={() => void saveRuleDraft()}
        >
          {saveRuleLoading ? "Saving..." : "Save rule"}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={cancelRuleEdit}>
          Cancel
        </Button>
        <span
          style={{ fontSize: 11, color: "#888", alignSelf: "center" }}
          id={`${idPrefix}-hint`}
        >
          Each save appends a new revision.
        </span>
      </div>
    </div>
  );

  if (prompt.isLoading) {
    return <div style={{ fontSize: 13, color: "#888" }}>Loading prompt...</div>;
  }
  if (prompt.isError || !prompt.data || !active) {
    return <Alert variant="danger">{loadFailedMessage}</Alert>;
  }

  return (
    <div className="d-flex flex-column gap-3" style={{ fontSize: 13 }}>
      <div style={{ color: "#555" }}>{description}</div>

      <div style={{ fontSize: 12, color: "#888" }}>
        Active: {revisionLabel(active)} - {active.ruleCount} rule
        {active.ruleCount === 1 ? "" : "s"}, ~
        {Math.round(active.promptText.length / 4).toLocaleString()} tokens
      </div>

      {/* A deploy that ships a new built-in prompt resets every environment to it,
          discarding saved revisions built on the older one. Say so explicitly: the rules
          below are NOT what this operator last saved, and without this the editor would
          silently show different rules than they left. Their revision is still readable
          in the history, so a deliberate edit can be re-applied on top of the new
          default. */}
      {supersededRevision && (
        <Alert
          variant="warning"
          style={{ fontSize: 12, padding: "6px 10px", marginBottom: 0 }}
        >
          Running the shipped default
          {seedVersion !== undefined ? ` v${seedVersion}` : ""}. Revision{" "}
          {supersededRevision.revisionNumber}
          {supersededRevision.seedVersion !== null
            ? `, based on v${supersededRevision.seedVersion},`
            : ","}{" "}
          saved by {supersededRevision.createdBy} was superseded when a newer
          default shipped. Its rules are still in the history below - re-save them
          on top of the new default if you want them back.
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {note && <Alert variant="success">{note}</Alert>}

      {/* Preamble ------------------------------------------------------ */}
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <strong>Preamble</strong>
          {preambleChanged && (
            <span style={{ fontSize: 12, color: "#b8860b" }}>
              Unsaved changes
            </span>
          )}
        </div>
        <Form.Control
          as="textarea"
          rows={12}
          value={preambleDraft}
          onChange={(e) => setPreambleDraft(e.target.value)}
          style={{ fontFamily: "monospace", fontSize: 12 }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <Button
            size="sm"
            variant="primary"
            disabled={!preambleChanged || writing}
            onClick={() => void savePreamble()}
          >
            {savePreambleLoading ? "Saving..." : "Save preamble"}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={writing}
            onClick={resetToDefault}
          >
            Reset whole prompt to shipped default
          </Button>
        </div>
      </div>

      {/* Rules --------------------------------------------------------- */}
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <strong>Rules</strong>
          <Button size="sm" variant="outline-primary" onClick={startAdd}>
            + Add rule
          </Button>
        </div>
        {addingRule && (
          <div style={{ marginBottom: 8 }}>{renderRuleEditor("add")}</div>
        )}
        {rules.length === 0 && !addingRule ? (
          <div style={{ fontSize: 12, color: "#666" }}>
            {emptyRulesHint ?? (
              <>
                No rules on this revision - the whole prompt is in the preamble
                above. Use <strong>+ Add rule</strong> to break it out into
                addressable rules, or{" "}
                <strong>Reset whole prompt to shipped default</strong> to adopt
                the shipped rule set.
              </>
            )}
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {rules.map((r, index) => (
              <div
                key={r.rule_id}
                style={{
                  border: "1px solid #eee",
                  borderRadius: 4,
                  padding: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <strong>{r.rule_id}</strong>
                  {r.title && <span>{r.title}</span>}
                  <Badge bg={statusVariant(r.status)}>{r.status}</Badge>
                  {r.category && (
                    <span style={{ fontSize: 11, color: "#888" }}>
                      {r.category}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: 11, padding: 0 }}
                    disabled={index === 0 || writing}
                    onClick={() => void moveRule(index, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: 11, padding: 0 }}
                    disabled={index === rules.length - 1 || writing}
                    onClick={() => void moveRule(index, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: 11, padding: 0 }}
                    onClick={() =>
                      editingRuleId === r.rule_id
                        ? cancelRuleEdit()
                        : startEdit(r)
                    }
                  >
                    {editingRuleId === r.rule_id ? "Close" : "Edit"}
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: 11, padding: 0, color: "#b02a37" }}
                    disabled={writing}
                    onClick={() => void removeRule(r.rule_id)}
                  >
                    Delete
                  </Button>
                </div>
                {editingRuleId === r.rule_id ? (
                  <div style={{ marginTop: 8 }}>
                    {renderRuleEditor(r.rule_id)}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#444", marginTop: 4 }}>
                    {r.guidance}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enforced footer ---------------------------------------------- */}
      <div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
          Always appended (fixed, not editable) - data-residency guardrail:
        </div>
        <pre
          style={{
            background: "#f6f6f6",
            border: "1px solid #eee",
            borderRadius: 4,
            padding: 8,
            fontSize: 11,
            whiteSpace: "pre-wrap",
            margin: 0,
            color: "#555",
          }}
        >
          {prompt.data.enforcedFooter}
        </pre>
      </div>

      {/* Compiled preview --------------------------------------------- */}
      <CollapsibleCard title={compiledPreviewLabel} initialOpen={false}>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            fontSize: 11,
            margin: 0,
            color: "#333",
            maxHeight: 400,
            overflow: "auto",
          }}
        >
          {active.promptText}
        </pre>
      </CollapsibleCard>

      {/* History + diff ----------------------------------------------- */}
      <div>
        <strong>Revision history</strong>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
            marginTop: 4,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <Form.Select
              size="sm"
              value={restoreOf || ""}
              onChange={(e) => setRestoreOf(Number(e.target.value))}
            >
              <option value="" disabled>
                Select a revision...
              </option>
              {prompt.data.revisions.map((r) => (
                <option key={r.revisionNumber} value={r.revisionNumber}>
                  {revisionLabel(r)}
                  {r.note ? ` - ${r.note}` : ""}
                </option>
              ))}
            </Form.Select>
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={!restoreOf || restoreOf === active.revisionNumber || writing}
            onClick={restore}
          >
            Restore
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={!restoreOf}
            onClick={() => setDiffTo(restoreOf)}
          >
            View diff vs previous
          </Button>
        </div>
      </div>

      {/* Test panel (task-specific) ----------------------------------- */}
      <hr style={{ margin: "4px 0" }} />
      {draftAddendum && renderTestPanel(draftAddendum)}

      {/* Diff modal --------------------------------------------------- */}
      <Modal
        show={diffTo !== null}
        onHide={() => setDiffTo(null)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            Diff: revision {diffFrom ?? "-"} → {diffTo}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {diffFrom === null ? (
            <div style={{ fontSize: 12, color: "#888" }}>
              Revision {diffTo} is the earliest revision - nothing to diff
              against.
            </div>
          ) : diffQuery.isLoading ? (
            <div style={{ fontSize: 12, color: "#888" }}>Loading diff...</div>
          ) : diffQuery.isError || !diffQuery.data ? (
            <Alert variant="danger">Could not load the diff.</Alert>
          ) : (
            <>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 13 }}>Rule changes</strong>
                {diffQuery.data.preambleChanged && (
                  <div style={{ fontSize: 12, color: "#0a58ca" }}>
                    Preamble edited
                  </div>
                )}
                {diffQuery.data.ruleChanges.length === 0 &&
                !diffQuery.data.preambleChanged ? (
                  <div style={{ fontSize: 12, color: "#666" }}>No changes.</div>
                ) : (
                  <ul style={{ fontSize: 12, marginBottom: 0 }}>
                    {diffQuery.data.ruleChanges.map((c, i) => (
                      <li key={`${c.rule_id}-${c.kind}-${i}`}>
                        <strong>{c.rule_id}</strong>
                        {c.title ? ` - ${c.title}` : ""}: {c.kind}
                        {c.detail ? ` (${c.detail})` : ""}
                        {c.fields_changed.length > 0
                          ? ` [${c.fields_changed.join(", ")}]`
                          : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <strong style={{ fontSize: 13 }}>Text diff</strong>
                <pre
                  style={{
                    fontSize: 11,
                    whiteSpace: "pre-wrap",
                    margin: 0,
                    maxHeight: 320,
                    overflow: "auto",
                  }}
                >
                  {diffQuery.data.textDiff.map((line, i) => (
                    <div
                      key={i}
                      style={{
                        color: line.startsWith("+")
                          ? "#0a7d24"
                          : line.startsWith("-")
                            ? "#b02a37"
                            : "#666",
                      }}
                    >
                      {line}
                    </div>
                  ))}
                </pre>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PromptRuleSetEditor;
